import { RADAR_FRAME } from "@divespots/contracts";
import type { RadarFrame } from "@divespots/contracts";
import type { Map as LeafletMap } from "leaflet";
import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { L } from "../leaflet-global.ts";
import { registerPrecipHook } from "../precip/e2eHook.ts";
import { precipColors } from "../precip/raster.ts";
import type { Rgba } from "../precip/raster.ts";
import { frameImage, radarLut } from "./raster.ts";

/** The precipitation pane (forecast and radar never draw at the same time). */
const PANE = "precip";
const PANE_Z = 340;
/** Cross-fade between two frames while playing; shorter than RADAR_PLAY_FRAME_MS. */
const FADE_MS = 250;

const readToken = (name: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/**
 * Canvas layer of the measured radar: one frame, drawn from its 640 x 440 cells straight into a
 * viewport-sized canvas with the browser's smoothing. The frame grid is uniform in Web Mercator,
 * so the image maps onto the map with a plain scale and offset (no skew). Two canvases allow a
 * cross-fade while playing; without a frame (loading, failed) nothing is drawn.
 */
export class RadarCanvasLayer extends L.Layer {
  private canvases: [HTMLCanvasElement, HTMLCanvasElement] | null = null;
  private front = 0;
  private mapRef: LeafletMap | null = null;
  private frame: RadarFrame | null = null;
  private source: HTMLCanvasElement | null = null;
  private sourceCanvas: HTMLCanvasElement | null = null;
  private pixels: Uint8ClampedArray<ArrayBuffer> | null = null;
  private colors: Rgba[] = precipColors(readToken);
  private drawnPx = 0;
  private classCounts = [0, 0, 0, 0];
  private scheme: MediaQueryList | null = null;

  /** Coloured cells of the frame on show (E2E hook). */
  get drawn(): number {
    return this.drawnPx;
  }
  get byClass(): number[] {
    return this.classCounts;
  }
  /** Time of the frame on show, null while none. */
  get frameTimeMs(): number | null {
    return this.frame?.timeMs ?? null;
  }

  override onAdd(map: LeafletMap): this {
    this.mapRef = map;
    const pane = map.getPane(PANE) ?? map.createPane(PANE);
    pane.style.zIndex = String(PANE_Z);
    pane.style.pointerEvents = "none";
    const make = () => {
      const c = document.createElement("canvas");
      c.className = "precip-canvas radar-canvas leaflet-zoom-hide";
      c.setAttribute("aria-hidden", "true");
      c.style.opacity = "0";
      pane.appendChild(c);
      return c;
    };
    this.canvases = [make(), make()];
    map.on("moveend zoomend resize", this.redraw, this);
    this.scheme = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null;
    this.scheme?.addEventListener?.("change", this.recolor);
    map.whenReady(() => this.show(false));
    return this;
  }

  override onRemove(map: LeafletMap): this {
    map.off("moveend zoomend resize", this.redraw, this);
    this.scheme?.removeEventListener?.("change", this.recolor);
    this.scheme = null;
    this.canvases?.forEach((c) => c.remove());
    this.canvases = null;
    this.mapRef = null;
    this.source = null;
    this.sourceCanvas = null;
    this.pixels = null;
    this.drawnPx = 0;
    return this;
  }

  /** Show a frame (null: clear); `fade` cross-fades from the one on show. */
  setFrame(frame: RadarFrame | null, fade: boolean): void {
    if (frame === this.frame) return;
    this.frame = frame;
    this.buildSource();
    this.show(fade);
  }

  recolor = (): void => {
    this.colors = precipColors(readToken);
    this.buildSource();
    this.show(false);
  };

  private buildSource(): void {
    const frame = this.frame;
    if (!frame) {
      this.source = null;
      this.drawnPx = 0;
      this.classCounts = [0, 0, 0, 0];
      return;
    }
    // one source canvas and one pixel buffer for every frame shown
    const { width, height } = RADAR_FRAME;
    const src = (this.sourceCanvas ??= document.createElement("canvas"));
    src.width = width;
    src.height = height;
    const pixels = (this.pixels ??= new Uint8ClampedArray(width * height * 4));
    const { drawn, byClass } = frameImage(frame, radarLut(this.colors), pixels);
    src.getContext("2d")?.putImageData(new ImageData(pixels, width, height), 0, 0);
    this.source = src;
    this.drawnPx = drawn;
    this.classCounts = byClass;
  }

  /** Draw the frame into the hidden canvas, then swap (with or without the fade). */
  private show(fade: boolean): void {
    if (!this.canvases) return;
    const back = this.canvases[1 - this.front]!;
    const front = this.canvases[this.front]!;
    this.paint(back);
    // no cross-fade to nothing: a failed or loading frame must not leave the old picture under its label
    const t =
      fade && this.source && !matchMedia("(prefers-reduced-motion: reduce)").matches ? FADE_MS : 0;
    for (const c of [back, front]) c.style.transition = t ? `opacity ${t}ms linear` : "none";
    back.style.opacity = this.source ? "1" : "0";
    front.style.opacity = "0";
    this.front = 1 - this.front;
  }

  private redraw = (): void => {
    if (!this.canvases) return;
    this.paint(this.canvases[this.front]!);
    this.canvases[1 - this.front]!.getContext("2d")?.clearRect(0, 0, 1e5, 1e5);
  };

  private paint(canvas: HTMLCanvasElement): void {
    const map = this.mapRef;
    if (!map) return;
    const s = map.getSize();
    if (s.x === 0 || s.y === 0) return;
    canvas.width = s.x;
    canvas.height = s.y;
    canvas.style.width = `${s.x}px`;
    canvas.style.height = `${s.y}px`;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    const src = this.source;
    const ctx = canvas.getContext("2d");
    if (!src || !ctx) return;
    // where the whole frame lands in the viewport; only the visible part of it is drawn
    const nw = map.latLngToContainerPoint([RADAR_FRAME.north, RADAR_FRAME.west]);
    const se = map.latLngToContainerPoint([RADAR_FRAME.south, RADAR_FRAME.east]);
    const dw = se.x - nw.x;
    const dh = se.y - nw.y;
    if (!(dw > 0 && dh > 0)) return;
    const x0 = Math.max(0, nw.x);
    const y0 = Math.max(0, nw.y);
    const x1 = Math.min(s.x, se.x);
    const y1 = Math.min(s.y, se.y);
    if (x1 <= x0 || y1 <= y0) return;
    const kx = RADAR_FRAME.width / dw;
    const ky = RADAR_FRAME.height / dh;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(
      src,
      (x0 - nw.x) * kx,
      (y0 - nw.y) * ky,
      (x1 - x0) * kx,
      (y1 - y0) * ky,
      x0,
      y0,
      x1 - x0,
      y1 - y0,
    );
  }
}

interface Props {
  /** The frame to draw; null (loading, failed) draws nothing. */
  frame: RadarFrame | null;
  /** Cross-fade to the next frame (playing, motion allowed). */
  fade: boolean;
  aerial: boolean;
}

/** react-leaflet child of the overview map: mounted while a radar step is shown. */
export function RadarLayer({ frame, fade, aerial }: Props) {
  const map = useMap();
  const layer = useRef<RadarCanvasLayer | null>(null);

  useEffect(() => {
    const l = new RadarCanvasLayer();
    layer.current = l;
    l.addTo(map);
    const unregister = registerPrecipHook(l, () => ({
      mode: "radar",
      frameTime: l.frameTimeMs,
      drawn: l.drawn,
      byClass: l.byClass,
    }));
    return () => {
      unregister();
      l.remove();
      layer.current = null;
    };
  }, [map]);

  useEffect(() => layer.current?.setFrame(frame, fade), [frame, fade]);
  useEffect(() => layer.current?.recolor(), [aerial]);
  return null;
}
